import {
  VENDOR_IMPORT_INDIVIDUAL_REASON_MAX,
  vendorImportTileOf,
  type PlanVendorImportResponse,
  type VendorImportApprovedRow,
  type VendorImportLinkCandidate,
  type VendorImportOmittableAliasColumn,
  type VendorImportPlanAlias,
  type VendorImportPlanRow,
} from '@clm/shared-types';

/**
 * The Admin's per-row decisions on a vendor-import plan — CLM-113 (R-113-5,
 * R-113-8, R-113-12, R-113-14, R-113-16, R-113-17, R-113-26; §3.4, §4.7).
 *
 * Pure: the surface keeps one {@link RowDecision} per `row_ref` in component
 * state (never browser storage — the plan holds vendor names and addresses),
 * and every rule about what a ticked row still needs lives here, so it is
 * tested on arrays rather than through the DOM.
 *
 * ## What the server re-checks, and what this only mirrors
 *
 * Execute recomputes the plan and refuses an approval that does not hold with
 * a `422` at the row's path (`action_not_offered`, `short_name_unconfirmed`,
 * `shared_alias_unconfirmed`, …). This module asks the same questions first so
 * the Import button is disabled while a ticked row is incomplete; it never
 * relaxes one. A name the plan ties to an earlier row of the sheet
 * (`other_row_refs`, R-113-17) is asked for while that row is ticked onto
 * another party; where it still cannot know the server's answer exactly, it
 * asks the Admin rather than guessing "no".
 *
 * ## Defaults (§4.7)
 *
 * `create` and `update` rows — the writes the plan itself proposes — start
 * ticked. Decision rows and individuals start unticked: an undecided row writes
 * nothing (R-113-12). Rejected, empty and unchanged rows have no control.
 */

/** `''` — not chosen yet; `party:<key>` / `row:<row_ref>` — a `link` target. */
export type RowChoice = '' | 'create' | 'update' | 'skip' | `party:${string}` | `row:${string}`;

export interface RowDecision {
  /** Ticked: this row is in the execute. */
  readonly include: boolean;
  readonly choice: RowChoice;
  /** R-113-16. */
  readonly confirm_short_names: boolean;
  /** R-113-17 / R-114-7 — "Keep this name on both parties". */
  readonly accept_shared_aliases: boolean;
  /** R-113-5 — required to include an individual; sent trimmed. */
  readonly individual_reason: string;
  /** R-113-14, R-113-24 — Name 2 / Search Name aliases left out; durable once executed. */
  readonly omit_aliases: readonly VendorImportOmittableAliasColumn[];
  /**
   * The last execute was refused at this row for a confirmation the plan gave
   * this page no way to foresee (`422 shared_alias_unconfirmed` /
   * `short_name_unconfirmed` — e.g. a name an earlier row of the same execute
   * now holds). The control is then shown and required, so the Admin can always
   * answer the server; a new choice clears it.
   */
  readonly server_asks: readonly ('accept_shared_aliases' | 'confirm_short_names')[];
}

export type Decisions = Readonly<Record<string, RowDecision>>;

export type ChoiceOption =
  | { readonly kind: 'link'; readonly value: `party:${string}` | `row:${string}`; readonly candidate: VendorImportLinkCandidate }
  | { readonly kind: 'update'; readonly value: 'update' }
  | { readonly kind: 'create'; readonly value: 'create' }
  | { readonly kind: 'skip'; readonly value: 'skip' };

const WRITING_ACTIONS: ReadonlySet<string> = new Set(['create', 'update', 'link']);

/**
 * A row the Admin can act on: it offers an action that writes and is not
 * already as the sheet says. A rejected row offers nothing (it cannot be
 * included); an unchanged or empty row has nothing to write.
 */
export function isDecidable(row: VendorImportPlanRow): boolean {
  const tile = vendorImportTileOf(row);
  if (tile === 'unchanged' || tile === 'rejected' || tile === 'empty_record') return false;
  return row.offered_actions.some((action) => WRITING_ACTIONS.has(action));
}

/**
 * The `link` target a candidate stands for, or `null` when it cannot be one.
 * Only a Vendor can be linked (R-113-8): a Client or Partner match is shown,
 * never offered. An earlier row that already has a reference is linked through
 * its party; one that does not, through its row (it must then be imported as a
 * new vendor in the same execute).
 */
export function candidateChoice(candidate: VendorImportLinkCandidate): `party:${string}` | `row:${string}` | null {
  if (candidate.party_type !== 'Vendor') return null;
  if (candidate.party_key !== null) return `party:${candidate.party_key}`;
  if (candidate.row_ref !== null) return `row:${candidate.row_ref}`;
  return null;
}

/** What the row can be, in the order the control lists them: each link target, update, a new vendor, skip. */
export function choicesFor(row: VendorImportPlanRow): readonly ChoiceOption[] {
  if (!isDecidable(row)) return [];
  const options: ChoiceOption[] = [];
  if (row.offered_actions.includes('link')) {
    const seen = new Set<string>();
    for (const candidate of row.link_candidates) {
      const value = candidateChoice(candidate);
      if (value === null || seen.has(value)) continue;
      seen.add(value);
      options.push({ kind: 'link', value, candidate });
    }
  }
  if (row.offered_actions.includes('update')) options.push({ kind: 'update', value: 'update' });
  if (row.offered_actions.includes('create')) options.push({ kind: 'create', value: 'create' });
  options.push({ kind: 'skip', value: 'skip' });
  return options;
}

/** A decision row always asks; any other row asks only when it could be more than one thing. */
export function showsChoice(row: VendorImportPlanRow): boolean {
  if (!isDecidable(row)) return false;
  if (vendorImportTileOf(row) === 'decision_required') return true;
  return choicesFor(row).filter((option) => option.kind !== 'skip').length > 1;
}

export function actionOf(choice: RowChoice): 'create' | 'update' | 'link' | null {
  if (choice === 'create' || choice === 'update') return choice;
  if (choice.startsWith('party:') || choice.startsWith('row:')) return 'link';
  return null;
}

export function initialDecision(row: VendorImportPlanRow): RowDecision {
  const tile = vendorImportTileOf(row);
  const writes = choicesFor(row).filter((option) => option.kind !== 'skip');
  const only = writes.length === 1 && tile !== 'decision_required' ? writes[0] : undefined;
  return {
    include: tile === 'create' || tile === 'update',
    choice: tile === 'create' || tile === 'update' ? tile : (only?.value ?? ''),
    confirm_short_names: false,
    accept_shared_aliases: false,
    individual_reason: '',
    omit_aliases: [],
    server_asks: [],
  };
}

/** Fresh decisions for a fresh plan — a decision is never carried onto a row whose plan may have changed (§4.7). */
export function initialDecisions(plan: PlanVendorImportResponse): Record<string, RowDecision> {
  const decisions: Record<string, RowDecision> = {};
  for (const row of plan.rows) {
    if (isDecidable(row)) decisions[row.row_ref] = initialDecision(row);
  }
  return decisions;
}

/** A new choice ticks the row (or unticks it, for Skip) and asks for the shared-name acceptance afresh. */
export function withChoice(decision: RowDecision, choice: RowChoice): RowDecision {
  return {
    ...decision,
    choice,
    include: choice !== '' && choice !== 'skip',
    accept_shared_aliases: false,
    server_asks: [],
  };
}

/** The last execute was refused at this row for `confirmation` (a `422` the plan could not foresee). */
export function withServerAsk(decision: RowDecision, confirmation: RowDecision['server_asks'][number]): RowDecision {
  return decision.server_asks.includes(confirmation)
    ? decision
    : { ...decision, server_asks: [...decision.server_asks, confirmation] };
}

export function withInclude(decision: RowDecision, include: boolean): RowDecision {
  return { ...decision, include, choice: include && decision.choice === 'skip' ? '' : decision.choice };
}

/** The aliases the Admin may leave out: a new Name 2 or Search Name (a Name never; nothing on an individual's row). */
export function omittableAliases(row: VendorImportPlanRow): readonly VendorImportPlanAlias[] {
  if (row.is_individual || row.aliases === null) return [];
  return row.aliases.filter(
    (alias) =>
      (alias.column === 'name_2' || alias.column === 'search_name') &&
      (alias.status === 'new' || alias.status === 'shared_with_other_party'),
  );
}

/** The short names the row would import (R-113-16), or `null` when the row is not flagged. An individual's are never listed. */
export function shortNamesOf(row: VendorImportPlanRow): readonly string[] | null {
  if (!row.flags.includes('short_name_confirmation_required')) return null;
  return (row.aliases ?? []).filter((alias) => alias.text.trim().length < 5).map((alias) => alias.text);
}

/** "Confirm this short name" is asked: the plan flagged the row, or the server refused it for want of one. */
export function needsShortConfirmation(row: VendorImportPlanRow, decision: RowDecision): boolean {
  return row.flags.includes('short_name_confirmation_required') || decision.server_asks.includes('confirm_short_names');
}

export type SharedRequirement =
  | { readonly kind: 'none' }
  /** "A new vendor" on a row matched by name: picking it is the acceptance, and the page says so (§3.4). */
  | { readonly kind: 'auto' }
  /** "Keep this name on both parties" must be ticked, or the shared Name 2 / Search Name left out. */
  | { readonly kind: 'confirm'; readonly names: readonly string[] };

const NONE: SharedRequirement = { kind: 'none' };

/**
 * Whether the row, as decided, would write a name another party holds
 * (R-113-17, R-114-7). The server re-runs the same test against the party the
 * row will actually write: a `link` adds only the names its target lacks, and
 * "another party" excludes that target (an `update`'s target is its own
 * party). A `create` on a row matched **by name** shares at least the matched
 * name; one matched only by PAN shares none (R-113-26). A name-matched earlier
 * row with no party yet holds the name only once it is ticked as a new vendor
 * in this execute — the server simulates the execute in sheet order the same
 * way.
 */
export function sharedRequirement(row: VendorImportPlanRow, decision: RowDecision, decisions: Decisions): SharedRequirement {
  const action = actionOf(decision.choice);
  if (action === null) return NONE;
  const byName = row.link_candidates.filter((candidate) => candidate.matched_on !== 'sheet_row_pan');
  if (action === 'create' && byName.length > 0) return { kind: 'auto' };

  const targetParty = decision.choice.startsWith('party:')
    ? decision.choice.slice('party:'.length)
    : action === 'update'
      ? (row.existing_party?.party_key ?? null)
      : null;
  /* An earlier row with no party yet holds the name once it is imported onto a party other than this row's target. */
  const earlierRowHolds = (rowRef: string): boolean => {
    const earlier = decisions[rowRef];
    if (earlier === undefined || !earlier.include || actionOf(earlier.choice) === null) return false;
    return targetParty === null || earlier.choice !== `party:${targetParty}`;
  };
  const names = new Set<string>();
  for (const alias of row.aliases ?? []) {
    /* A pending proposal this row would confirm is a write too: shared when another party holds it (R-114-2). */
    if (alias.status !== 'shared_with_other_party' && alias.status !== 'pending_will_confirm') continue;
    if (alias.column !== 'name' && decision.omit_aliases.includes(alias.column)) continue;
    if (alias.other_party_keys.some((key) => key !== targetParty) || alias.other_row_refs.some(earlierRowHolds)) {
      names.add(alias.text);
    }
  }

  let heldElsewhere = decision.server_asks.includes('accept_shared_aliases');
  for (const candidate of byName) {
    if (candidate.row_ref === null || candidate.party_key !== null) {
      /* A party, or an earlier row with its own reference: another holder unless it is the target. */
      if (candidate.party_key !== null && candidate.party_key !== targetParty) heldElsewhere = true;
    } else if (decision.choice !== `row:${candidate.row_ref}`) {
      /* An earlier row with no party yet holds the name only if it becomes a new vendor in this execute. */
      const earlier = decisions[candidate.row_ref];
      if (earlier !== undefined && earlier.include && earlier.choice === 'create') heldElsewhere = true;
    }
  }

  if (names.size === 0 && !heldElsewhere) return NONE;
  if (names.size === 0 && row.display_name !== null) names.add(row.display_name);
  return { kind: 'confirm', names: [...names] };
}

/** What a ticked row still needs before it can be imported. */
export type RowGap =
  | 'choose'
  | 'reason'
  | 'reason_too_long'
  | 'short_names'
  | 'shared_names'
  | 'earlier_row_not_created';

/**
 * An individual already included earlier, whose row now plans an `update` (§3.4
 * rule 9 on an individual's reference): the person is in CLM with a recorded
 * reason, so a routine re-upload asks for none and sends none, and the server
 * keeps the recorded one (R-113-5). A reason typed here replaces it. A row that
 * is newly an individual (`now_individual_in_sheet`) is not this: approving it
 * is the inclusion, and needs its reason.
 */
export function isIncludedIndividual(row: VendorImportPlanRow): boolean {
  return row.is_individual && row.offered_actions.includes('update') && !row.flags.includes('now_individual_in_sheet');
}

export function gapsOf(row: VendorImportPlanRow, decision: RowDecision | undefined, decisions: Decisions): readonly RowGap[] {
  if (decision === undefined || !decision.include) return [];
  const gaps: RowGap[] = [];
  if (actionOf(decision.choice) === null) gaps.push('choose');
  if (row.is_individual) {
    const reason = decision.individual_reason.trim();
    if (reason === '' && !isIncludedIndividual(row)) gaps.push('reason');
    else if (reason.length > VENDOR_IMPORT_INDIVIDUAL_REASON_MAX) gaps.push('reason_too_long');
  }
  if (needsShortConfirmation(row, decision) && !decision.confirm_short_names) gaps.push('short_names');
  if (sharedRequirement(row, decision, decisions).kind === 'confirm' && !decision.accept_shared_aliases) {
    gaps.push('shared_names');
  }
  if (decision.choice.startsWith('row:')) {
    const earlier = decisions[decision.choice.slice('row:'.length)];
    if (earlier === undefined || !earlier.include || earlier.choice !== 'create') gaps.push('earlier_row_not_created');
  }
  return gaps;
}

/** The rows that are ticked, in sheet order. */
export function includedRows(plan: PlanVendorImportResponse, decisions: Decisions): readonly VendorImportPlanRow[] {
  return plan.rows.filter((row) => isDecidable(row) && decisions[row.row_ref]?.include === true);
}

/** Every ticked row is complete. */
export function allComplete(plan: PlanVendorImportResponse, decisions: Decisions): boolean {
  return includedRows(plan, decisions).every((row) => gapsOf(row, decisions[row.row_ref], decisions).length === 0);
}

/**
 * The execute request's `approved` list, in sheet order (the order the server
 * mints keys in). Built only from complete ticked rows; each confirmation is
 * sent only where the plan raised the flag it answers.
 */
export function approvedRows(plan: PlanVendorImportResponse, decisions: Decisions): VendorImportApprovedRow[] {
  const approved: VendorImportApprovedRow[] = [];
  for (const row of includedRows(plan, decisions)) {
    const decision = decisions[row.row_ref];
    if (decision === undefined || gapsOf(row, decision, decisions).length > 0) continue;
    const action = actionOf(decision.choice);
    if (action === null) continue;
    const shared = sharedRequirement(row, decision, decisions);
    const omittable = new Set(omittableAliases(row).map((alias) => alias.column));
    const omit = decision.omit_aliases.filter((column) => omittable.has(column));
    const common = {
      row_ref: row.row_ref,
      ...(needsShortConfirmation(row, decision) && decision.confirm_short_names ? { confirm_short_names: true } : {}),
      ...(shared.kind === 'auto' || (shared.kind === 'confirm' && decision.accept_shared_aliases) ? { accept_shared_aliases: true } : {}),
      /* An already-included individual sends a reason only when the Admin typed a new one, so the recorded one is kept. */
      ...(row.is_individual && (!isIncludedIndividual(row) || decision.individual_reason.trim() !== '')
        ? { include_individual_reason: decision.individual_reason.trim() }
        : {}),
      ...(omit.length > 0 ? { omit_aliases: omit } : {}),
    };
    if (action === 'link') {
      approved.push(
        decision.choice.startsWith('party:')
          ? { action: 'link', ...common, party_key: decision.choice.slice('party:'.length) }
          : { action: 'link', ...common, same_as_row_ref: decision.choice.slice('row:'.length) },
      );
    } else {
      approved.push({ action, ...common });
    }
  }
  return approved;
}

/** Rows CLM already holds that the source now blocks are listed first (R-113-6); otherwise sheet order. */
export function orderedRows(rows: readonly VendorImportPlanRow[]): readonly VendorImportPlanRow[] {
  const first = rows.filter((row) => row.flags.includes('privacy_blocked_after_import'));
  if (first.length === 0) return rows;
  return [...first, ...rows.filter((row) => !row.flags.includes('privacy_blocked_after_import'))];
}
